import { Dayjs, ManipulateType } from 'dayjs';
import { OrderTicket, OrderTicketList } from '../../types';
import { ResponseType as SaleTicket } from '@shared/ticket/get/types';
import {
    DISTRIBUTION_TYPE,
    ORDER_STATUS,
    QR_TYPE,
    REMAINING_ORDER_DISTRIBUTION_STATUS,
    RESERVATION_AVAILABLE_UNIT,
    RESERVATION_STATUS,
    TICKET_STATUS,
    TICKET_TYPE,
    USAGE_TICKET_STATUS,
} from '../Constants';
import { dayjsExt } from './dayjsExtended';
import { isEmptyObject, isNumber } from '../validationUtils';

/**
 * 現在時刻が時間内であること.
 * 24時を跨ぐ場合は startTime-23:59:59, 0:00:00-endTimeの中にあること
 */
export const isInStartEnd = ({
    startTime,
    endTime,
}: {
    startTime?: string | null;
    endTime?: string | null;
}): boolean => {
    if (!startTime || !endTime) {
        return true;
    }
    const currentTime = dayjsExt().tz('Asia/Tokyo').format('HH:mm:ss');

    const isAfterStart = !startTime || currentTime >= startTime;
    const isBeforeEnd = !endTime || currentTime <= endTime;

    // 24時を跨ぐ処理かどうか
    const isCrossingMidnight = !!startTime && !!endTime && startTime > endTime;

    if (!isCrossingMidnight) {
        return isAfterStart && isBeforeEnd;
    } else {
        return isAfterStart || isBeforeEnd;
    }
};

export const isAfterReservationDisplayStartTime = (reservationTicket: any) => {
    if (!reservationTicket) {
        return false;
    }
    const { reservationDisplayStart, reservationDisplayStartUnit, orderUsageTickets } = reservationTicket;

    const reservationInfo = orderUsageTickets[0]?.reservationInfo;
    const reserveOn = reservationInfo?.reserveOn ?? orderUsageTickets[0]?.reserveOn;
    const reservationStatus = reservationInfo?.reservationStatus ?? orderUsageTickets[0]?.reservationStatus;
    if (!reserveOn || reservationStatus !== RESERVATION_STATUS.RESERVED) {
        return false;
    }

    const compareReserveUnit = reservationDisplayStartUnit === 'minutes' ? 'minute' : 'day';

    const reservationDisplayStartTime = dayjsExt(reserveOn)
        .subtract(reservationDisplayStart ?? 0, compareReserveUnit)
        .startOf(compareReserveUnit);

    return !dayjsExt().isBefore(reservationDisplayStartTime);
};

export const isBeforeReservationDisplayEndTime = (reservationTicket: any) => {
    if (!reservationTicket) {
        return false;
    }
    const { usingPeriod, usingPeriodUnit, orderUsageTickets } = reservationTicket;

    const reservationInfo = orderUsageTickets[0]?.reservationInfo;
    const reserveOn = reservationInfo?.reserveOn ?? orderUsageTickets[0]?.reserveOn;
    const reservationStatus = reservationInfo?.reservationStatus ?? orderUsageTickets[0]?.reservationStatus;
    if (!reserveOn || reservationStatus !== RESERVATION_STATUS.RESERVED) {
        return false;
    }

    const compareUsingUnit = usingPeriodUnit === 'm' ? 'minute' : 'day';

    const reservationDisplayEndTime =
        compareUsingUnit === 'day'
            ? dayjsExt(reserveOn)
                  .add(usingPeriod ?? 0, compareUsingUnit)
                  .set('hour', 3)
                  .set('minute', 59)
                  .set('second', 59)
            : dayjsExt(reserveOn).add(usingPeriod ?? 0, compareUsingUnit);

    return !dayjsExt().isAfter(reservationDisplayEndTime);
};

/**
 * 操作時刻に使用可能かどうか
 * 使用方法：0,2
 * @param {Object} orderTicket
 * @returns {Boolean}
 */
export const canActivateNow = (orderTicket?: OrderTicket | OrderTicketList): boolean => {
    if (!orderTicket) {
        return false;
    }
    const { status, activateDeadlineAt, orderStatus } = orderTicket;
    const {
        enableActivateWeekdays,
        availableStartAt,
        availableEndAt,
        enableActivateDates,
        disableActivateDates,
        comboItems,
        holidays,
        qrType,
    } = orderTicket;

    // 使用前であること。
    const isBought = status === TICKET_STATUS.BOUGHT && orderStatus === ORDER_STATUS.BOUGHT;
    if (!isBought) {
        return false;
    }

    // 操作時刻の取得
    const now = dayjsExt();
    const currentDate = now.format('YYYY-MM-DD');
    const dayOfWeek = now.format('dddd').toLowerCase();

    const reservationTicket = comboItems?.find((item) => item.isReserve);
    if (isEmptyObject(reservationTicket)) {
        // 利用開始可能期限のチェック
        const isBeforeActivateDeadline = now.isBefore(dayjsExt(activateDeadlineAt));

        // 使用可能日かのチェック
        const isEnableActivateDate = (() => {
            // 使用開始可能日のチェック
            if (enableActivateDates?.includes(currentDate)) {
                return true;
            }
            // 使用開始不可能日のチェック
            if (disableActivateDates?.includes(currentDate)) {
                return false;
            }
            // 祝日チェック
            const isHoliday = holidays?.some((holiday) =>
                dayjsExt(holiday.holidayOn).isSame(dayjsExt(currentDate), 'day'),
            );
            if (isHoliday) {
                return enableActivateWeekdays.includes('holiday');
            }

            // 使用開始許可曜日のチェック
            return enableActivateWeekdays.includes(dayOfWeek);
        })();

        // 有効化可能時間のチェック
        const isInEnableActivateTime = isInStartEnd({ startTime: availableStartAt, endTime: availableEndAt });

        return isBeforeActivateDeadline && isEnableActivateDate && isInEnableActivateTime;
    } else {
        return (
            (isAfterReservationDisplayStartTime(reservationTicket) || qrType === QR_TYPE.SINGLE_SCAN) &&
            isBeforeReservationDisplayEndTime(reservationTicket)
        );
    }
};

/**
 * 操作時刻にチケット表示可能かどうか
 * 使用方法:0
 * @param {Object} orderTicket
 * @returns {Boolean}
 */
export const canDisplayNow = (orderTicket: any): boolean => {
    if (!orderTicket) {
        return false;
    }
    let order = orderTicket;
    const { comboItems } = order;

    // 子券 or 単券であること
    if (comboItems && comboItems.length > 1) {
        return false;
    }

    // 単券の省略が行われていない場合
    if (!order?.orderUsageTickets) {
        order = { ...order, ...comboItems[0] };
    }

    const { availableStartAt, displayEndAt, orderUsageTickets } = order;
    const { expiredAt, status } = orderUsageTickets![0];

    // 使用中であること
    if (status !== USAGE_TICKET_STATUS.ACTIVATED) {
        return false;
    }

    // 操作時刻の取得
    const now = dayjsExt.tz();

    // 有効期限のチェック
    if (!expiredAt) {
        console.error('Expired Time is undefined'); // 有効期限がない場合のこと。
    }
    const isBeforeExpired = now.isBefore(dayjsExt(expiredAt));

    // 表示可能時間のチェック
    const isInDisplayAbleTime = isInStartEnd({
        startTime: availableStartAt,
        endTime: displayEndAt,
    });

    return isBeforeExpired && isInDisplayAbleTime;
};

/**
 * 購入済みチケット一覧画面に表示するチケットかどうかの判定
 * まだ使えるチケットを表示する。
 */
export const isUsableTicket = (orderTicket?: OrderTicket | OrderTicketList) => {
    if (!orderTicket) {
        return false;
    }

    const { isCombination, comboItems, orderStatus, distributionType } = orderTicket;

    // ユーザー払戻承認後も購入済みチケット一覧画面に表示する
    if (orderStatus === ORDER_STATUS.USER_REFUND_ENABLED || orderStatus === ORDER_STATUS.STOCK_USER_REFUND_ENABLED) {
        return true;
    }

    // 購入状態のチェック
    if (orderStatus !== ORDER_STATUS.BOUGHT) {
        return false;
    }

    if (comboItems?.length < 1) {
        console.error('no comboItems');
        return false;
    }

    // 使用可能な子券であるかどうかの判定
    const isUsableChildTicket = (childTicket: any) => {
        const { remainingOrderDistribution } = childTicket;
        const { remainingOrderDistributionStatus } = remainingOrderDistribution ?? {};
        const now = dayjsExt();

        //企画券
        if (childTicket.ticketType === TICKET_TYPE.TICKET) {
            const usageTicket = childTicket.orderUsageTickets[0];

            // 分配可能チケットの場合分配残数情報をチェック
            if (
                distributionType === DISTRIBUTION_TYPE.ENABLE &&
                remainingOrderDistributionStatus === REMAINING_ORDER_DISTRIBUTION_STATUS.USED
            ) {
                return false;
            }

            //予約あり
            if (childTicket.isReserve) {
                //チケットの使用状態が「購入済み」もしくは「有効化済み」である。
                if (
                    usageTicket.status !== USAGE_TICKET_STATUS.BOUGHT &&
                    usageTicket.status !== USAGE_TICKET_STATUS.ACTIVATED &&
                    usageTicket.status !== USAGE_TICKET_STATUS.PARTIALLY_USED
                ) {
                    return false;
                }
                //予約状態が「予約済み」もしくは「キャンセル済み」である。
                if (
                    usageTicket.reservationStatus !== RESERVATION_STATUS.RESERVED &&
                    usageTicket.reservationStatus !== RESERVATION_STATUS.CANCELED
                ) {
                    return false;
                }
                //操作時刻がチケットの予約利用日時と有効期間から算出される表示可能終了時刻より前の時刻である。
                const reserveOn = usageTicket.reserveOn;
                if (!reserveOn) {
                    return false;
                }
                const { usingPeriod, usingPeriodUnit } = childTicket;
                const compareUsingUnit = usingPeriodUnit === 'm' ? 'minute' : 'day';
                const reservationDisplayEndTime =
                    compareUsingUnit === 'day'
                        ? dayjsExt(reserveOn)
                              .add(usingPeriod ?? 0, compareUsingUnit)
                              .set('hour', 3)
                              .set('minute', 59)
                              .set('second', 59)
                        : dayjsExt(reserveOn).add(usingPeriod ?? 0, compareUsingUnit);

                return !now.isAfter(reservationDisplayEndTime);
            } else {
                //予約なし
                switch (usageTicket.status) {
                    case USAGE_TICKET_STATUS.BOUGHT:
                        return now.isBefore(dayjsExt(childTicket.activateDeadlineAt));
                    case USAGE_TICKET_STATUS.ACTIVATED:
                        return now.isBefore(dayjsExt(usageTicket.expiredAt));
                    default:
                        return false;
                }
            }
        } else {
            //綴り券
            return childTicket.orderUsageTickets.some((coupon: any) => {
                switch (coupon.status) {
                    case USAGE_TICKET_STATUS.BOUGHT:
                        return now.isBefore(dayjsExt(childTicket.activateDeadlineAt));
                    case USAGE_TICKET_STATUS.ACTIVATED:
                        return now.isBefore(dayjsExt(coupon.expiredAt));
                    default:
                        return false;
                }
            });
        }
    };

    //セット券場合は
    if (isCombination) {
        return comboItems.some((item) => {
            if (item.status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED) return false;
            return isUsableChildTicket(item);
        });
    } else {
        //子券や単券場合は
        const childTicket = comboItems[0];
        if (childTicket.status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED) return false;
        return isUsableChildTicket(childTicket);
    }
};

/**
 * 使用期限切れかどうかを判定する。
 */
export const isAfterActivateDeadline = (activateDeadlineAt?: string | Date | null) => {
    if (activateDeadlineAt == null) {
        return false;
    }

    // 操作時刻の取得
    const now = dayjsExt();
    return now.isAfter(dayjsExt(activateDeadlineAt));
};

/**
 * 有効期限切れかどうかを判定する。
 */
export const isAfterExpired = (expiredAt?: string | Date | null) => {
    if (expiredAt == null) {
        return false;
    }

    // 操作時刻の取得
    const now = dayjsExt();
    return now.isAfter(dayjsExt(expiredAt));
};

/**
 * 販売期間内かどうかを判定する。
 */
export const isOnSaleTicket = (ticket: SaleTicket) => {
    // 操作時刻の取得
    const now = dayjsExt();

    const isAfterSaleStart = now.isAfter(dayjsExt(ticket.saleStartDate));
    const isBeforeSaleEnd = !ticket?.saleEndDate || now.isBefore(dayjsExt(ticket.saleEndDate));
    return isAfterSaleStart && isBeforeSaleEnd && ticket.isSale;
};

/**
 * 日本時間での時刻フォーマットを行う
 * @param {*} dateTime
 * @param {string} format
 * @returns
 */
export const formatJpDate = (dateTime?: Dayjs | string | Date | null, format?: string) => {
    if (dateTime == null || !format) {
        return null;
    }
    return dayjsExt(dateTime).tz().format(format);
};

/**
 * 日本時間で時刻の加算を行う
 * @param {*} dateTime - 基準となる時刻
 * @param {number} number
 * @param {string} unit
 * @returns
 */
export const addJpDate = (
    dateTime: Dayjs | Date | string | null | undefined,
    number: number,
    unit?: ManipulateType,
): Dayjs | null => {
    if (dateTime == null || !isNumber(number) || !unit) {
        return null;
    }
    return dayjsExt(dateTime).tz().add(number, unit);
};

/**
 * 予約開始時刻を返す関数
 * @param currentDate 現在の時刻
 * @param value N(日/月)前から予約可能
 * @param unit 時間単位と設定規則 "days" | "months_with_day" | "months_without_day"
 * @returns 予約開始時刻
 */
export const calReservationAvailableEndAt = (
    currentDate?: Dayjs | string | null,
    value?: number | null,
    unit?: ManipulateType | null,
) => {
    if (currentDate == null || value == null || !unit) {
        return null;
    }
    switch (unit) {
        case RESERVATION_AVAILABLE_UNIT.DAYS:
            return dayjsExt(currentDate).add(value, 'day').endOf('day');
        case RESERVATION_AVAILABLE_UNIT.MONTHS_WITH_DAY:
            return dayjsExt(currentDate).add(value, 'month').endOf('day');
        case RESERVATION_AVAILABLE_UNIT.MONTHS_WITHOUT_DAY:
            return dayjsExt(currentDate).add(value, 'month').endOf('month');
        default:
            return null;
    }
};

/**
 * 予約締切日時算出関数
 * @param reserveOn 予約時刻
 * @param value N(分/日)前に締め切るか
 * @param unit 時間の単位
 * @returns  予約締め切り時刻
 */
export const calReservationDeadLine = (reserveOn: string, value?: number, unit?: ManipulateType) => {
    if (!reserveOn || value == null || !unit) {
        return null;
    }
    return dayjsExt(reserveOn).subtract(value, unit).endOf(unit);
};
